import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DollarSign, CreditCard, Mail, Phone, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ParentPortalUpgradeGate = ({ portalData, onUpgradeInterest, upgradeLoading }) => {
  const [generatingLink, setGeneratingLink] = useState(false);
  const hasZelle = portalData.parentZelleEmail || portalData.parentZellePhone;
  const hasSquare = portalData.parentSquareEnabled;
  const hasPaymentOptions = hasZelle || hasSquare;

  const handlePayWithCard = async () => {
    setGeneratingLink(true);
    try {
      const res = await axios.post(`${API}/parent-communication/parent/generate-payment-link`);
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to generate payment link. Contact the center.");
    } finally {
      setGeneratingLink(false);
    }
  };

  return (
    <div className="grid gap-8 xl:grid-cols-[1fr_0.9fr]" data-testid="parent-upgrade-gate">
      <section className="relative overflow-hidden rounded-[2.2rem] border border-white/70 shadow-2xl">
        <img src={portalData.heroImageUrl} alt="Upgrade parent portal" className="absolute inset-0 h-full w-full object-cover" data-testid="parent-upgrade-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c5a57]/92 via-[#146e88]/80 to-[#ef8b27]/50" />
        <div className="relative space-y-5 px-6 py-8 text-white lg:px-10 lg:py-10">
          <Badge className="bg-white/15 text-white" data-testid="parent-upgrade-trial-badge">{portalData.trialLabel}</Badge>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/80" data-testid="parent-upgrade-eyebrow">Trial ended</p>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl" data-testid="parent-upgrade-title">Your parent access is ready to upgrade.</h1>
            <p className="mt-3 max-w-2xl text-sm text-white/90 sm:text-base" data-testid="parent-upgrade-description">
              Your trial has ended. Use the payment options on the right to continue your access through {portalData.centerName}.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-[2rem] bg-white p-6 shadow-xl ring-1 ring-slate-200/80 space-y-5" data-testid="parent-upgrade-card">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">Continue access</p>
          <h2 className="mt-2 text-3xl font-bold text-slate-900" data-testid="parent-upgrade-price">{portalData.monthlyAddOnLabel}</h2>
        </div>

        {hasPaymentOptions ? (
          <div className="space-y-3">
            {hasZelle && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-3" data-testid="parent-upgrade-zelle">
                <p className="font-semibold text-blue-700 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> Pay via Zelle
                </p>
                {portalData.parentZelleEmail && (
                  <p className="text-sm text-slate-700 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    Send to: <strong>{portalData.parentZelleEmail}</strong>
                  </p>
                )}
                {portalData.parentZellePhone && (
                  <p className="text-sm text-slate-700 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    Or phone: <strong>{portalData.parentZellePhone}</strong>
                  </p>
                )}
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="parent-upgrade-zelle-note">
                  <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">Zelle payments are manually confirmed by the center. Your access will be activated within 1 business day after payment is received.</p>
                </div>
              </div>
            )}

            {hasSquare && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="parent-upgrade-square">
                <p className="font-semibold text-amber-700 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Pay by Credit / Debit Card
                </p>
                <Button
                  onClick={handlePayWithCard}
                  disabled={generatingLink}
                  className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="parent-upgrade-card-pay-button"
                >
                  {generatingLink ? "Opening checkout..." : "Pay with Card — activates instantly"}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600" data-testid="parent-upgrade-copy">
              Contact {portalData.centerName} to activate full access.
            </p>
            <Button
              onClick={onUpgradeInterest}
              disabled={upgradeLoading}
              className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="parent-upgrade-interest-button"
            >
              {upgradeLoading ? "Saving..." : "I'm interested in continuing"}
            </Button>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full"
          onClick={() => window.location.href = `mailto:${portalData.centerContactEmail}`}
          data-testid="parent-upgrade-contact-button"
        >
          Contact {portalData.centerName}
        </Button>

        <div className="rounded-[1.5rem] bg-slate-50 p-4" data-testid="parent-upgrade-feature-list">
          <p className="font-semibold text-slate-900">What unlocks after payment</p>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li>Messages and staff replies</li>
            <li>Documents, forms, and uploads</li>
            <li>Billing, invoices, and announcements</li>
          </ul>
        </div>
      </section>
    </div>
  );
};
